import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _taggedTemplateLiteral from "@babel/runtime/helpers/esm/taggedTemplateLiteral";
var _excluded = ["customTagElement", "dataTestId", "hideText", "href", "icon", "iconClassName", "iconSize", "isDisabled", "isLoading", "loaderText", "onClick", "onKeyDown", "preserveClickableArea", "size", "stateStyles", "text", "type"];

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6;

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

import React from 'react';
import { ICON_SIZE } from "../../icons";
import { KEY_CODES, preventDefaultBehavior } from "../../utilities";
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { BUTTON_SIZES } from "../constants";
import { NormalizedButton } from "../elements";
import { ElementStateWrapper, getRem, transitionAnimation, typographyButton } from "./../../core";
import { ButtonContent } from "./ButtonContent";
import { jsx as _jsx } from "react/jsx-runtime";

var getButtonSizeStyles = function getButtonSizeStyles(size, margin) {
  return css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  height: ", ";\n  min-width: ", ";\n  ", "\n"])), size, size, !!margin && css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    margin-bottom: ", ";\n    margin-top: ", ";\n  "])), margin, margin));
};

var getButtonSize = function getButtonSize(theme, size, preserveClickableArea) {
  switch (size) {
    case BUTTON_SIZES.SMALL:
      return getButtonSizeStyles(getRem(32), preserveClickableArea ? theme.size.spacing.medium.value : null);

    case BUTTON_SIZES.LARGE:
      return getButtonSizeStyles(getRem(40), preserveClickableArea ? theme.size.spacing.small.value : null);

    default:
      return getButtonSizeStyles(getRem(36), preserveClickableArea ? getRem(6) : null);
  }
};

var StyledBaseButton = styled(NormalizedButton)(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  ", "\n  ", "\n    align-items: center;\n  border-radius: ", ";\n  box-shadow: none;\n  cursor: ", ";\n  display: inline-flex;\n  justify-content: center;\n  margin: 0;\n  padding: 0 ", ";\n  position: relative;\n  text-decoration: none;\n  ", "\n\n  &::after {\n    ", "\n    border-radius: ", ";\n    box-sizing: border-box;\n    content: '';\n    display: block;\n    height: 100%;\n    left: 0;\n    position: absolute;\n    top: 0;\n    width: 100%;\n    z-index: ", ";\n  }\n\n  ", "\n\n  ", "\n\n    ", "\n"])), function (_ref) {
  var theme = _ref.theme;
  return typographyButton(theme);
}, transitionAnimation('background-color, box-shadow, color, transform'), function (_ref2) {
  var theme = _ref2.theme;
  return getRem(theme.size.borderRadius.large.value);
}, function (_ref3) {
  var isDisabled = _ref3.isDisabled;
  return isDisabled ? 'not-allowed' : 'pointer';
}, getRem(12), function (_ref4) {
  var preserveClickableArea = _ref4.preserveClickableArea,
      size = _ref4.size,
      theme = _ref4.theme;
  return getButtonSize(theme, size, preserveClickableArea);
}, transitionAnimation('background-color, border-color'), function (_ref5) {
  var theme = _ref5.theme;
  return getRem(theme.size.borderRadius.large.value);
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.zIndex.buttonOverlay.value;
}, function (_ref7) {
  var isLoading = _ref7.isLoading;
  return isLoading && css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n      cursor: progress;\n      min-width: ", ";\n    "])), getRem(68));
}, function (_ref8) {
  var hasIcon = _ref8.hasIcon,
      theme = _ref8.theme;
  return hasIcon && css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n      padding-left: ", ";\n    "])), theme.size.spacing.medium.value);
}, function (_ref9) {
  var showOnlyIcon = _ref9.showOnlyIcon;
  return showOnlyIcon && css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n      padding-left: 0;\n      padding-right: 0;\n    "])));
});
var BaseButton = /*#__PURE__*/React.forwardRef(function (_ref10, ref) {
  var customTagElement = _ref10.customTagElement,
      dataTestId = _ref10.dataTestId,
      hideText = _ref10.hideText,
      href = _ref10.href,
      icon = _ref10.icon,
      iconClassName = _ref10.iconClassName,
      iconSize = _ref10.iconSize,
      isDisabled = _ref10.isDisabled,
      isLoading = _ref10.isLoading,
      loaderText = _ref10.loaderText,
      onClick = _ref10.onClick,
      onKeyDown = _ref10.onKeyDown,
      preserveClickableArea = _ref10.preserveClickableArea,
      size = _ref10.size,
      stateStyles = _ref10.stateStyles,
      text = _ref10.text,
      type = _ref10.type,
      other = _objectWithoutProperties(_ref10, _excluded);

  var isLink = !!href;
  var defaultTag = isLink ? 'a' : 'button';
  var Tag = customTagElement || defaultTag;

  var handleClick = function handleClick(event) {
    if (isDisabled) {
      event.preventDefault();
    } else {
      onClick(event);
    }
  };

  var attributes = _objectSpread({}, other);

  if (!customTagElement) {
    if (isLink) {
      attributes.href = !isDisabled ? href : undefined;
      attributes['aria-disabled'] = isDisabled;
      attributes.onClick = isDisabled ? function (event) {
        return event.preventDefault();
      } : undefined;
    } else {
      attributes.disabled = isDisabled;
      attributes.type = type || 'button';
      attributes.onClick = handleClick;
    }
  }

  var handleKeyDown = function handleKeyDown(event) {
    var isSpaceClicked = event.keyCode === KEY_CODES.SPACE;
    var shouldOpen = isLink && isSpaceClicked && !isDisabled;

    if (shouldOpen) {
      preventDefaultBehavior(event);
      event.target.click();
    }

    onKeyDown(event);
  };

  return /*#__PURE__*/_jsx(ElementStateWrapper, _objectSpread(_objectSpread({
    isDisabled: isDisabled,
    stateStyles: stateStyles
  }, attributes), {}, {
    children: /*#__PURE__*/_jsx(StyledBaseButton, {
      as: Tag,
      "data-testid": dataTestId,
      hasIcon: !!icon,
      isDisabled: isDisabled,
      isLoading: isLoading,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      preserveClickableArea: preserveClickableArea,
      ref: ref,
      showOnlyIcon: !!icon && hideText,
      size: size,
      children: /*#__PURE__*/_jsx(ButtonContent, {
        dataTestId: dataTestId,
        hideText: hideText,
        icon: icon,
        iconClassName: iconClassName,
        iconSize: iconSize,
        isLoading: isLoading,
        loaderText: loaderText,
        text: text
      })
    })
  }));
});
BaseButton.propTypes = {
  /** Replaces default tag: button or anchor with new value */
  customTagElement: PropTypes.string,

  /** Id value used for testing */
  dataTestId: PropTypes.string,

  /** If true, will visually hide text */
  hideText: PropTypes.bool,

  /** When URL is provided, element changes from button to hyperlink <a> */
  href: PropTypes.string,

  /** If not loading, will render specified icon before text */
  icon: PropTypes.node,

  /** Adds new class for icon element */
  iconClassName: PropTypes.string,

  /** Will set size of the icon */
  iconSize: PropTypes.oneOf(Object.values(ICON_SIZE)),

  /** Disallows user to interact with the button */
  isDisabled: PropTypes.bool,

  /** If true, will display loader and adjust width to better accommodate spinner */
  isLoading: PropTypes.bool,

  /** Loader text which will be read for screen reader users */
  loaderText: PropTypes.node,

  /** Callback that is called on click */
  onClick: PropTypes.func,

  /** Callback that is called on key down */
  onKeyDown: PropTypes.func,

  /** If true, will add vertical margins to the component */
  preserveClickableArea: PropTypes.bool,

  /** Changes button height */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),

  /** Specifies element state styles */
  stateStyles: PropTypes.shape({
    /** Specifies :active pseudo state styles */
    active: PropTypes.array.isRequired,

    /** Specifies default styles that are always applied */
    "default": PropTypes.array.isRequired,

    /** Specifies styles when isDisabled is true */
    disabled: PropTypes.array.isRequired,

    /** Specifies :focus pseudo state styles */
    focus: PropTypes.array.isRequired,

    /** Specifies :hover pseudo state styles */
    hover: PropTypes.array.isRequired
  }),

  /** Will display text inside button */
  text: PropTypes.node.isRequired,

  /** Default html button 'type' attribute values when button component is used */
  type: PropTypes.string
};
BaseButton.defaultProps = {
  customTagElement: '',
  dataTestId: undefined,
  hideText: false,
  href: undefined,
  icon: undefined,
  iconClassName: '',
  iconSize: ICON_SIZE.DEFAULT,
  isDisabled: false,
  isLoading: false,
  loaderText: 'Loading...',
  onClick: function onClick() {},
  onKeyDown: function onKeyDown() {},
  preserveClickableArea: false,
  size: BUTTON_SIZES.STANDARD,
  stateStyles: {
    active: [],
    "default": [],
    disabled: [],
    focus: [],
    hover: []
  },
  type: ''
};
export { BaseButton };